import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import axios from "axios";
import { API_DETAILS, COOKIE_KEYS } from "../../App";

/**
 * What the patient has been charged, and what was refunded.
 *
 * Every figure comes from user_subscription_history — the same rows Stripe's
 * webhooks write — so this cannot drift from what was actually billed.
 */

const api = (path) => API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension + path;

// Kept in step with MembershipPanel: the API returns the plan enum, the
// marketing name lives in the browser.
const MARKETING_NAMES = {
  STANDARD: "Youro One",
  PREMIUM: "Youro Plus",
  ONETIME: "Youro On Demand",
};

const card = {
  padding: "24px",
  borderRadius: "10px",
  backgroundColor: "var(--card)",
  border: "1px solid var(--border)",
  marginBottom: "24px",
};

const heading = {
  fontFamily: "Nunito, sans-serif",
  fontSize: "20px",
  fontWeight: 600,
  lineHeight: 1.4,
  color: "var(--youro-charcoal)",
  margin: 0,
};

const th = {
  textAlign: "left",
  fontSize: "13px",
  fontWeight: 600,
  color: "var(--youro-charcoal)",
  opacity: 0.7,
  padding: "0 12px 8px 0",
  whiteSpace: "nowrap",
};

const td = {
  fontSize: "14px",
  color: "var(--youro-charcoal)",
  padding: "12px 12px 12px 0",
  borderTop: "1px solid var(--border)",
  verticalAlign: "top",
};

/** Amounts arrive in cents, exactly as Stripe reports them. */
const money = (cents) =>
  typeof cents === "number"
    ? `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : null;

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const planLabel = (name) => MARKETING_NAMES[name] || name || "Membership";

/**
 * One badge covering both the payment and the refund, because a refunded charge
 * is the thing a patient is actually looking for and it should not be buried in
 * a second column they have to cross-reference.
 */
const StatusBadge = ({ row }) => {
  let label = row.paymentStatus || "—";
  let bg = "var(--youro-sand)";
  let color = "var(--youro-charcoal)";

  if (row.isPromoGrant) {
    label = "Included";
    bg = "var(--youro-mint)";
    color = "var(--youro-sage)";
  } else if (row.refundStatus === "REFUNDED" || row.refundAmount > 0) {
    label = "Refunded";
    bg = "var(--severity-high-bg)";
    color = "var(--severity-high-text)";
  } else if (row.paymentStatus === "PAID" || row.paymentStatus === "SUCCEEDED") {
    label = "Paid";
    bg = "var(--youro-mint)";
    color = "var(--youro-sage)";
  }

  return (
    <span style={{
      display: "inline-block",
      padding: "2px 10px",
      borderRadius: "999px",
      fontSize: "12px",
      fontWeight: 500,
      backgroundColor: bg,
      color,
      whiteSpace: "nowrap",
    }}>
      {label}
    </span>
  );
};

const PaymentHistoryPanel = () => {
  const uId = Cookies.get(COOKIE_KEYS.userId);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!uId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    axios
      .get(api(`/getPaymentHistory/${uId}`))
      .then((res) => {
        if (!cancelled) setRows(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.error("Could not load payment history:", err);
        // Money is worth an explicit failure state: an empty table would read as
        // "you were never charged", which is a different and alarming claim.
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [uId]);

  return (
    <div style={card}>
      <h3 style={heading}>Payment History</h3>

      {loading && (
        <p style={{ margin: "16px 0 0", fontSize: "14px", opacity: 0.7 }}>Loading…</p>
      )}

      {!loading && failed && (
        <p style={{ margin: "16px 0 0", fontSize: "14px", color: "var(--youro-coral)" }}>
          We couldn't load your payment history just now. Please refresh to try again.
        </p>
      )}

      {!loading && !failed && rows.length === 0 && (
        <p style={{ margin: "16px 0 0", fontSize: "14px", opacity: 0.7 }}>
          No payments yet. Charges appear here once you start a membership.
        </p>
      )}

      {!loading && !failed && rows.length > 0 && (
        <>
          <div style={{ overflowX: "auto", marginTop: "16px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "480px" }}>
              <thead>
                <tr>
                  <th style={th}>Date</th>
                  <th style={th}>Plan</th>
                  <th style={th}>Amount</th>
                  <th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td style={td}>{formatDate(row.purchasedAt)}</td>
                    <td style={td}>{planLabel(row.planName)}</td>
                    <td style={td}>
                      {row.isPromoGrant ? "—" : money(row.amountTotal) ?? "—"}
                      {row.refundAmount > 0 && (
                        <div style={{ fontSize: "12px", opacity: 0.75, marginTop: "2px" }}>
                          {money(row.refundAmount)} refunded {formatDate(row.refundAt)}
                        </div>
                      )}
                    </td>
                    <td style={td}><StatusBadge row={row} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p style={{ margin: "16px 0 0", fontSize: "13px", opacity: 0.7 }}>
            Refunds are issued by our team rather than automatically. If you think
            a refund is due, contact support.
          </p>
        </>
      )}
    </div>
  );
};

export default PaymentHistoryPanel;
